import { secondaryStreamingApiConnector } from 'apiConnector/secondary-market/streaming';
import type { KeepAliveProductIdsSelector } from 'epic/secondaryMarket/realtimeEpic';
import { ofType } from 'redux-observable';
import { EMPTY, of } from 'rxjs';
import { catchError, filter, map, mergeMap, withLatestFrom } from 'rxjs/operators';
import {
  secondaryMarketPriceCleared,
  secondaryMarketPriceSubscribed,
  secondaryMarketPriceSubscribeFailed,
  secondaryMarketPriceUnsubscribeFailed,
} from 'state/secondaryMarket/realtime/realtime.action';
import { toBcsProductKey } from 'state/secondaryMarket/utils';
import type { EpicWithThunk } from 'state/state.common';
import type { BcsProductKey } from 'state/workspaces/secondary-market/quotes/secondaryMarketQuotes.model';

import { waitUntilConnectedToSignalR } from '../utils';

export function subscribeLiveStreamingEpic(): EpicWithThunk {
  return (
    action$,
    state$,
    {
      httpClient,
      selectors: {
        fromSecondaryQuotesWorkspace: { getProductKey },
      },
    },
  ) => {
    const { subscribe } = secondaryStreamingApiConnector(httpClient);

    const trigger$ = action$.pipe(
      ofType('SECONDARY_MARKET_LIVE_INIT_FORM_FETCHED', 'SECONDARY_MARKET_LIVE_RELOAD_FORM_FETCH_REQUESTED'),
      withLatestFrom(state$),
    );

    return waitUntilConnectedToSignalR(trigger$, action$).pipe(
      mergeMap(([{ tabId }, state]) => {
        const productKey: BcsProductKey | undefined = toBcsProductKey(getProductKey(state, tabId));
        if (productKey === undefined) {
          return EMPTY;
        }

        return subscribe({ productIds: [productKey], backend: 'Icom' }).pipe(
          map(() => secondaryMarketPriceSubscribed(productKey)),
          catchError(() => of(secondaryMarketPriceSubscribeFailed(productKey))),
        );
      }),
    );
  };
}

export function unsubscribeLiveStreamingEpic(
  ...keepAliveProductIdsSelectors: KeepAliveProductIdsSelector[]
): EpicWithThunk {
  return (action$, state$, { httpClient }) => {
    const { unsubscribe } = secondaryStreamingApiConnector(httpClient);
    return action$.pipe(
      ofType('SECONDARY_MARKET_LIVE_QUOTE_EXPIRED', 'SECONDARY_MARKET_QUOTES_WORKSPACE_REMOVE_TAB'),
      filter(a => {
        if (a.type === 'SECONDARY_MARKET_QUOTES_WORKSPACE_REMOVE_TAB') {
          return a.tabType === 'live';
        }
        return true;
      }),
      withLatestFrom(state$),
      mergeMap(([{ productKey }, state]) => {
        if (keepAliveProductIdsSelectors.some(selector => selector(state).has(productKey))) {
          return EMPTY;
        }
        const bcsProductKey = toBcsProductKey(productKey)!;
        return unsubscribe({ productIds: [bcsProductKey], backend: 'Icom' }).pipe(
          map(() => secondaryMarketPriceCleared(bcsProductKey)),
          catchError(() => of(secondaryMarketPriceUnsubscribeFailed(bcsProductKey))),
        );
      }),
    );
  };
}
